import { createSignal, JSXElement, Match, mergeProps, Switch } from 'solid-js';
import Base from '../Base.tsx';
import {
  Building2Icon,
  CheckIcon,
  ChevronRightIcon,
  DollarSignIcon,
  LayersIcon,
  SettingsIcon,
  UserPlusIcon
} from 'lucide-solid';
import Menu from '~/components/ui/Menu';
import { Localized } from '~/i18n';
import styles from '../switcher.module.scss';
import WorkspaceSwitcherMenuItem from '~/components/Layout/Header/SwitcherButtons/WorkspaceSwitcher/WorkspaceSwitcherMenuItem.tsx';
import { getLocalizedString } from '~/i18n/utils.ts';
import {
  AnyAppPath,
  getSlug,
  replacePathParams,
  WorkspacePagePath
} from '~/components/AppRouter/utils.ts';
import { ItemGroupType } from '~/components/ui/Menu/Menu.tsx';
import { isFeatureEnabled } from '~/utils/features/featureFlag.tsx';
import { FeatureFlagKeys } from '~/api/types/featureFlagData.ts';
import { useAppData } from '~/components/AppData';
import NamedIcon from '~/components/ui/NamedIcon';
import { getRandomColor } from '~/utils/commonUtils.ts';
import { APP_WORKSPACE_SWITCH_PATH } from '~/api';
import { BillingTabTypes } from '~/pages/Billing/utils.ts';
import SkLoader from '~/components/ui/SkLoader';

type WorkspaceSwitcherProps = {
  children: JSXElement;
};

const getWorkspaceSwitcherGroups = (): ItemGroupType[] => {
  const appData = useAppData();
  const workspaces = appData.member?.organizations;
  const [isWorkspaceLoading, setIsWorkspaceLoading] =
    createSignal<boolean>(false);
  const [selectedWorkspace, setSelectedWorkspace] = createSignal<string>();

  const switchWorkspace = (organizationId: string) => {
    window.location.assign(
      APP_WORKSPACE_SWITCH_PATH + `?organization_id=${organizationId}`
    );
  };

  const groups: ItemGroupType[] = [
    {
      items: [
        {
          label: (
            <WorkspaceSwitcherMenuItem
              iconComponent={SettingsIcon}
              label={getLocalizedString(`page_titles.settings`)}
              href={getSlug.workspace(
                WorkspacePagePath.WORKSPACE_GENERAL,
                true
              )}
            />
          ),
          dataTestId: 'workspace-settings'
        },
        {
          label: (
            <WorkspaceSwitcherMenuItem
              iconComponent={UserPlusIcon}
              label={getLocalizedString(`page_titles.members`)}
              href={getSlug.workspace(
                WorkspacePagePath.WORKSPACE_MEMBERS,
                true
              )}
            />
          ),
          dataTestId: 'workspace-team-members'
        }
      ]
    }
  ];
  if (isFeatureEnabled(FeatureFlagKeys.WORKSPACE_BILLING)) {
    groups[0].items!.push({
      label: (
        <WorkspaceSwitcherMenuItem
          iconComponent={DollarSignIcon}
          label={getLocalizedString(`page_titles.billing`)}
          href={replacePathParams(
            getSlug.workspace(
              WorkspacePagePath.WORKSPACE_BILLING,
              true
            ) as AnyAppPath,
            { tabType: BillingTabTypes.DETAILS }
          )}
        />
      ),
      dataTestId: 'workspace-billing'
    });
  } else if (isFeatureEnabled(FeatureFlagKeys.BILLING)) {
    groups[0].items!.push({
      label: (
        <WorkspaceSwitcherMenuItem
          iconComponent={DollarSignIcon}
          label={getLocalizedString(`page_titles.billing`)}
          href={getSlug.workspace(WorkspacePagePath.WORKSPACE_BILLING_V1, true)}
        />
      ),
      dataTestId: 'workspace-billing'
    });
  }
  groups[0].items!.push({
    label: (
      <div
        class={`flex items-center font-normal text-fg-default w-full h-full justify-between`}
      >
        <div class={`flex items-center`}>
          <LayersIcon
            class={`mr-2 text-fg-default w-5 h-5`}
            strokeWidth={1}
          />
          <Localized translationKey={'page_titles.switch_workspace'} />
        </div>
        <ChevronRightIcon
          strokeWidth={1}
          class={`w-4 h-4`}
        />
      </div>
    ),
    dataTestId: 'workspace-switch-workspace',
    subGroups: [
      {
        label: (
          <div
            class={`flex justify-between items-center ${styles.menuCategoryText}`}
          >
            <Localized
              translationKey={`top_nav_group_labels.your_workspaces`}
            />
          </div>
        ),
        items: workspaces?.map((workspace) => ({
          label: (
            <div
              class={`flex items-center justify-between w-full grow p-2`}
              classList={{
                'font-medium bg-bg-subtle border border-[#e8e8e8] rounded-md':
                  appData.member?.activeWorkspaceExternalId ===
                  workspace.organizationId,
                'font-normal hover:bg-bg-subtle':
                  appData.member?.activeWorkspaceExternalId !==
                  workspace.organizationId
              }}
            >
              <div class={`flex items-center gap-2`}>
                <NamedIcon
                  name={workspace?.displayName || workspace.name}
                  w={20}
                  color={getRandomColor()}
                  class={`border border-[var(--colors-accent-9)] text-fg-muted text-[10px]`}
                  fallbackIcon={
                    <Building2Icon
                      strokeWidth={1}
                      class={`min-w-[10px] min-h-[10px]`}
                    />
                  }
                />
                <span>
                  {workspace?.displayName ||
                    workspace.name ||
                    workspace.organizationId}
                </span>
              </div>
              <Switch>
                <Match
                  when={
                    appData.member?.activeWorkspaceExternalId ===
                    workspace.organizationId
                  }
                >
                  <CheckIcon
                    class={`ml-auto w-[20px] h-[20px] !text-[#34A569]`}
                  />
                </Match>
                <Match
                  when={
                    isWorkspaceLoading() &&
                    workspace.organizationId === selectedWorkspace()
                  }
                >
                  <div class={`relative mr-1 mt-3`}>
                    <SkLoader />
                  </div>
                </Match>
              </Switch>
            </div>
          ),
          class: ``,
          onClick: () => {
            if (
              appData.member?.activeWorkspaceExternalId !==
              workspace.organizationId
            ) {
              setIsWorkspaceLoading(true);
              setSelectedWorkspace(workspace.organizationId);
              switchWorkspace(workspace.organizationId);
            }
          }
        })),
        class: ``
      }
      // {
      //   label: (
      //     <Button
      //       class={`w-full justify-start px-2 hover:bg-bg-subtle`}
      //       variant={`ghost`}
      //       onClick={() => {}}
      //     >
      //       <div
      //         class={`flex items-center gap-2 text-fg-default font-normal self-start`}
      //       >
      //         <PlusIcon
      //           width={16}
      //           height={16}
      //           strokeWidth={1}
      //         />
      //         <Localized
      //           translationKey={`top_nav_group_labels.create_workspace`}
      //         />
      //       </div>
      //     </Button>
      //   ),
      //   class:
      //     'fixed w-[calc(100%+2rem)] bottom-0 z-10 bg-bg-default py-1 border-t border-border-subtle rounded-b-md font-medium'
      // }
    ],
    containerClass: `${styles.workspaceSubSwitcherMenu}`,
    subItemMenuCloseOnSelect: false,
    subItemMenuOffset: {
      mainAxis: 16
    }
  });
  return groups;
};

export default function WorkspaceSwitcher(
  props: WorkspaceSwitcherProps
): JSXElement {
  const merged = mergeProps({}, props);
  return (
    <Menu
      id={`workspace-switcher`}
      groups={getWorkspaceSwitcherGroups()}
      containerClass={`${styles.workspaceSwitcherMenu}`}
    >
      {(props) => (
        <Base
          elementAttributes={props}
          dataTestId="workspace-switcher-icon"
        >
          <span class={`mr-2`}>{merged.children}</span>
          <SettingsIcon
            strokeWidth={1}
            class={styles.headerSettingsIcon}
          />
        </Base>
      )}
    </Menu>
  );
}
